import { useEffect, useState } from "react";
import Icon from "@/components/feature/Icon";
import SmartImage from "@/components/feature/SmartImage";

function FloatingOrbs() {
  return (
    <>
      <div className="absolute top-[5%] right-[20%] w-72 h-72 rounded-full bg-white/[0.03] blur-3xl animate-float-2" />
      <div className="absolute bottom-[30%] left-[5%] w-60 h-60 rounded-full bg-silver-300/[0.03] blur-3xl animate-float-1" />
      <div className="absolute top-[50%] right-[5%] w-48 h-48 rounded-full bg-white/[0.02] blur-2xl animate-float-3" />
    </>
  );
}

function TechScanLine() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.04]">
      <div className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-white to-transparent animate-scan-line" />
    </div>
  );
}

function TechParticles() {
  const particles = [
    { left: "10%", top: "20%", delay: "0s", size: 2 },
    { left: "25%", top: "60%", delay: "1.2s", size: 3 },
    { left: "40%", top: "30%", delay: "2.1s", size: 2 },
    { left: "55%", top: "70%", delay: "0.8s", size: 1.5 },
    { left: "70%", top: "15%", delay: "1.5s", size: 2.5 },
    { left: "85%", top: "45%", delay: "2.8s", size: 2 },
    { left: "15%", top: "80%", delay: "3.2s", size: 1.5 },
    { left: "60%", top: "85%", delay: "1.8s", size: 2 },
    { left: "90%", top: "25%", delay: "2.5s", size: 3 },
    { left: "35%", top: "90%", delay: "0.5s", size: 1.5 },
  ];
  return (
    <div className="absolute inset-0 pointer-events-none hidden md:block">
      {particles.map((p, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-white/60 animate-tech-particle"
          style={{
            left: p.left,
            top: p.top,
            width: p.size,
            height: p.size,
            animationDelay: p.delay,
            animationDuration: `${3 + Math.random() * 3}s`,
          }}
        />
      ))}
    </div>
  );
}

export default function AboutHero() {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative bg-navy-950 overflow-hidden">
      <FloatingOrbs />
      <TechScanLine />
      <TechParticles />

      <div className="absolute inset-0">
        <SmartImage
          src="https://readdy.ai/api/search-image?query=Abstract%20futuristic%20digital%20technology%20background%20with%20flowing%20data%20streams%20and%20geometric%20hexagonal%20mesh%20patterns%20on%20deep%20dark%20navy%20background%2C%20luminous%20silver%20and%20white%20holographic%20interface%20elements%2C%20circuit-like%20connection%20lines%20and%20glowing%20nodes%2C%20high%20contrast%20minimalist%20corporate%20sci-fi%20aesthetic%2C%20no%20organic%20shapes%20or%20brain%20imagery%2C%20cinematic%20wide%20composition%20with%20dramatic%20lighting&width=1400&height=700&seq=about-hero-v3&orientation=landscape"
          alt="關於 TAIAA"
          className="w-full h-full object-cover opacity-40"
          loading="eager"
          hideOnError
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950/85 via-navy-950/65 to-navy-950/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-transparent to-navy-950/40" />
      </div>

      <div className="absolute inset-0 pointer-events-none opacity-[0.08]">
        <div
          className="w-full h-full animate-grid-move"
          style={{
            backgroundImage:
              "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
            backgroundSize: "100px 100px",
          }}
        />
      </div>

      <div className="relative z-10 px-6 md:px-10 lg:px-16 pt-32 pb-10 md:pt-40 md:pb-14">
        <div className="max-w-4xl">
          <p
            className={`text-sm font-medium tracking-[0.2em] text-silver-400 uppercase mb-4 transition-all duration-700 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            About TAIAA
          </p>
          <h1
            className={`font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-white leading-tight mb-6 transition-all duration-700 delay-150 animate-text-glow ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            關於協會
          </h1>
          <p
            className={`text-silver-300 text-base md:text-lg leading-relaxed max-w-2xl mb-8 transition-all duration-700 delay-300 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            台灣 AI 應用推廣協會由產學界領袖共同發起，致力於搭建產官學研合作橋樑，推動 AI 技術在台灣各產業的實際應用與永續發展。
          </p>

          <div
            className={`flex flex-wrap gap-4 transition-all duration-700 delay-[450ms] ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <a
              href="#mission"
              className="inline-flex items-center gap-2 bg-white text-navy-950 px-7 py-3.5 rounded-full text-sm font-medium hover:bg-silver-100 transition-colors whitespace-nowrap"
            >
              協會宗旨
              <Icon className="ri-arrow-down-line" />
            </a>
            <a
              href="#team"
              className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-3.5 rounded-full text-sm font-medium hover:bg-white/10 transition-colors whitespace-nowrap"
            >
              理監事團隊
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}